import { useCallback } from 'react';

import useAgentStore from '@/features/Agent/stores';
import useProductStore from '@/features/Product/store/ProductStore';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import { useFeedbackLanguageList } from '@/stores/LocaleStore';
import useTagStore from '@/stores/TagStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { type CATEGORY_TYPE, CategoryDic } from '@/types/categoryConst';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import {
  type FEEDBACK_STATUS,
  type FeedbackSentiment,
  FeedbackStatusMap,
  priorityMap,
  sentimentMap,
  type SOURCE_STATUS,
  SourceStatusMap,
} from '@/types/feedbackConst';
import {
  AGENT_COLUMNS,
  FEEDBACK_COLUMNS,
  PRODUCT_COLUMNS,
  SENTIMENT_COLUMNS,
} from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';

const useStructuredFilter = () => {
  const fullTopicMap = useTaxonomyStore((state) => state.fullTopicMap);
  const { productMap, brandMap, lineMap, productOptionMap } = useProductStore(
    (state) => {
      return {
        productMap: state.productMap,
        brandMap: state.brandMap,
        lineMap: state.lineMap,
        productOptionMap: state.productOptionMap,
      };
    },
  );
  const sourceInfo = useFeedbackSourceStore((state) => state.sourceInfo);
  const languageList = useFeedbackLanguageList();
  const agentMap = useAgentStore((state) => state.agentMap);
  const tagMap = useTagStore((state) => state.tagMap);

  const parseFilterKeyByColumnKey = useCallback(
    (
      filterKeys: StringNumberValue[],
      columnKey: string,
    ): {
      filterKey: StringNumberValue;
      filterName: string;
    }[] => {
      switch (columnKey) {
        case FEEDBACK_COLUMNS.CATEGORY:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: CategoryDic[filterKey as CATEGORY_TYPE],
          }));
        case SENTIMENT_COLUMNS.ENDING_SENTIMENT:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: sentimentMap[filterKey as FeedbackSentiment].value,
          }));
        case SENTIMENT_COLUMNS.KEY_SENTIMENT:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: sentimentMap[filterKey as FeedbackSentiment].value,
          }));
        case SENTIMENT_COLUMNS.SENTIMENT_DETAIL:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: sentimentMap[filterKey as FeedbackSentiment].value,
          }));
        case FEEDBACK_COLUMNS.PRIORITY:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: priorityMap[filterKey as PriorityKeyT].value,
          }));
        case FEEDBACK_COLUMNS.TICKET_STATUS:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: SourceStatusMap[filterKey as SOURCE_STATUS].value,
          }));
        case FEEDBACK_COLUMNS.STATUS:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: FeedbackStatusMap[filterKey as FEEDBACK_STATUS],
          }));
        case FEEDBACK_COLUMNS.HASHTAG:
          return filterKeys.map((filterKey) => ({
            filterKey,
            filterName: `#${filterKey.toString().replace(/^#+/, '')}`,
          }));
        case FEEDBACK_COLUMNS.MENTION:
          return filterKeys.map((filterKey) => ({
            filterKey,
            filterName: `@${filterKey.toString().replace(/^@+/, '')}`,
          }));
        case FEEDBACK_COLUMNS.LANGUAGE:
          return filterKeys.map((filterKey) => ({
            filterKey,
            filterName:
              languageList.find(
                (language) => language.code === filterKey.toString(),
              )?.name ?? filterKey.toString(),
          }));
        case FEEDBACK_COLUMNS.TAG:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              tagMap[filterKey as string]?.name ?? filterKey.toString(),
          }));
        case FEEDBACK_COLUMNS.TAXONOMY_TOPIC:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              fullTopicMap[filterKey as string]?.name ?? filterKey.toString(),
          }));
        case AGENT_COLUMNS.ALIAS:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              agentMap[filterKey as string]?.name ?? filterKey.toString(),
          }));

        case PRODUCT_COLUMNS.BRAND:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              brandMap.get(filterKey as string)?.name ?? filterKey.toString(),
          }));
        case PRODUCT_COLUMNS.LINE:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              lineMap.get(filterKey as string)?.name ?? filterKey.toString(),
          }));
        case PRODUCT_COLUMNS.PRODUCT:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              productMap.get(filterKey as string)?.product_name ??
              filterKey.toString(),
          }));
        case PRODUCT_COLUMNS.PRODUCT_OPTION:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName:
              productOptionMap.get(filterKey as string)?.option_name ??
              filterKey.toString(),
          }));

        default:
          return filterKeys.map((filterKey) => ({
            filterKey: filterKey,
            filterName: filterKey.toString(),
          }));
      }
    },
    [
      agentMap,
      brandMap,
      fullTopicMap,
      languageList,
      lineMap,
      productMap,
      productOptionMap,
      tagMap,
    ],
  );

  const parseStructuredFilter = useCallback(
    (structuredFilter: StructuredFilter): StructuredFilterQueryFormatT[] => {
      const sFilter = new SFilter(structuredFilter);

      const sourceFilterQueryFormats = sFilter.extractSourceFilter(
        sourceInfo || [],
      );
      const rangeFilterQueryFormats = sFilter.extractRangeFilter();

      const formats: StructuredFilterQueryFormatT[] = [
        ...sourceFilterQueryFormats,
        ...rangeFilterQueryFormats,
      ];

      const newStructuredFilter = sFilter.get();

      for (const [key, value] of Object.entries(newStructuredFilter || {})) {
        if (!value) continue;
        switch (key) {
          case FEEDBACK_COLUMNS.DATE_START:
          case FEEDBACK_COLUMNS.DATE_END:
            console.error('Date column must not be used in structuredFilter');
            continue;
          case 'block_analyzed':
            continue;

          default: {
            if (Array.isArray(value)) {
              for (const v of value) {
                formats.push({
                  columnKey: key,
                  filterValues: parseFilterKeyByColumnKey(
                    v.values as StringNumberValue[],
                    key,
                  ),
                  operator: v.op,
                });
              }
            } else {
              // 배열이 아닌 값(number, string)은 range/date 필터로 이미 처리되어야 하므로 skip
              continue;
            }
          }
        }
      }

      return formats;
    },
    [parseFilterKeyByColumnKey, sourceInfo],
  );

  return {
    parseStructuredFilter,
    parseFilterKeyByColumnKey,
  };
};

const convertFilterOperatorToStructuredFilterOperator = (
  operator: FILTER_OPERATOR,
): STRUCTURED_FILTER_OPERATOR => {
  switch (operator) {
    case FILTER_OPERATOR.OR:
      return STRUCTURED_FILTER_OPERATOR.OR;
    case FILTER_OPERATOR.AND:
      return STRUCTURED_FILTER_OPERATOR.AND;
  }
};

const convertStructuredFilterOperatorToFilterOperator = (
  operator: STRUCTURED_FILTER_OPERATOR,
): FILTER_OPERATOR => {
  switch (operator) {
    case STRUCTURED_FILTER_OPERATOR.OR:
      return FILTER_OPERATOR.OR;
    case STRUCTURED_FILTER_OPERATOR.AND:
      return FILTER_OPERATOR.AND;
    // case STRUCTURED_FILTER_OPERATOR.NAND:
    //   throw new Error('NAND operator is not supported');
    case STRUCTURED_FILTER_OPERATOR.NOR:
      throw new Error('NOR operator is not supported');
    default:
      throw new Error('Invalid structured filter operator');
  }
};

export {
  convertFilterOperatorToStructuredFilterOperator,
  convertStructuredFilterOperatorToFilterOperator,
};

export default useStructuredFilter;
